import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})

export class InventoryService {
  private baseURL: string;

  constructor(private http: HttpClient) {
    this.baseURL = environment.apiBaseUrl;
  }

  saveInventory(payload: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseURL}/Inventory/SaveInventory`,
      payload,
      { observe: 'response' }
    ).pipe(
      map((res: HttpResponse<any>) => res.body),
      catchError(this.handleError)
    );
  }

  checkStockIdExists(stockId: string, vendorId: number): Observable<any> {
    return this.http.get<any>(
      `${this.baseURL}/Inventory/CheckStockIdExists/${stockId}/${vendorId}`,
      { observe: 'response' }
    ).pipe(
      map((res: HttpResponse<any>) => res.body),
      catchError(this.handleError)
    );
  }

  getInventoryItemsByStockId(stockId: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseURL}/Inventory/GetInventoryItemsByStockId/${stockId}`,
      { observe: 'response' }
    ).pipe(
      map((res: HttpResponse<any>) => res.body),
      catchError(this.handleError)
    );
  }

  getInventoryStockForPOS(searchFieldId, searchValue, pageNumber: number = 1, pageSize: number = 100): Observable<any> {
    return this.http.get<any>(
      `${this.baseURL}/Inventory/GetInventoryStockForPOS?searchFieldId=${searchFieldId}&searchValue=${searchValue}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
      { observe: 'response' }
    ).pipe(
      map((res: HttpResponse<any>) => res.body),
      catchError(this.handleError)
    );
  }

  getPendingSaleInvoiceNoByStockId(stockId: string): Observable<number | null> {
    return this.http.get<any>(
      `${this.baseURL}/Inventory/GetPendingSaleInvoiceNoByStockId?stockId=${encodeURIComponent(stockId)}`,
      { observe: 'response' }
    ).pipe(
      map((res: HttpResponse<any>) => res.body),
      catchError(this.handleError)
    );
  }


  getInventoryByInwardNumber(inwardNo: number): Observable<any> {
    return this.http.get<any>(
      `${this.baseURL}/Inventory/GetInventoryByInwardNumber/${inwardNo}`,
      { observe: 'response' }
    ).pipe(
      map((res: HttpResponse<any>) => res.body),
      catchError(this.handleError)
    );
  }

  getInventoryStockInwardDetails(
    searchFieldId: number,
    searchValue?: string,
    stockReceiptDate?: Date,
    pageNumber: number = 0,
    pageSize: number = 100
  ): Observable<any> {

    let params: any = {
      searchFieldId: searchFieldId.toString(),
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString()
    };

    if (searchValue) {
      params.searchValue = searchValue;
    }

    if (stockReceiptDate) {
      const yyyy = stockReceiptDate.getFullYear();
      const mm = (stockReceiptDate.getMonth() + 1).toString().padStart(2, '0');
      const dd = stockReceiptDate.getDate().toString().padStart(2, '0');

      params.stockReceiptDate = `${yyyy}-${mm}-${dd}`;
    }

    return this.http.get<any>(
      `${this.baseURL}/Inventory/GetInventoryStockInwardDetails`,
      {
        params,
        observe: 'response'
      }
    ).pipe(
      map((res: HttpResponse<any>) => res.body),
      catchError(this.handleError)
    );
  }

  getInventoryStockByVendor(vendorId: number, field: number, value?: string, fromDate?: string, toDate?: string): Observable<any> {
    let params = `?field=${field}`;
    if (value) params += `&value=${encodeURIComponent(value)}`;
    if (fromDate) params += `&fromDate=${encodeURIComponent(fromDate)}`;
    if (toDate) params += `&toDate=${encodeURIComponent(toDate)}`;
    return this.http.get<any>(
      `${this.baseURL}/Inventory/GetInventoryStockByVendor/${vendorId}${params}`,
      { observe: 'response' }
    ).pipe(
      map(res => res.body),
      catchError(this.handleError)
    );
  }

  updateInventoryStockDetails(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseURL}/Inventory/UpdateInventoryStockDetails`,
      data,
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }
    ).pipe(
      map(res => res),
      catchError(this.handleError)
    );
  }

  returnInventoryItem(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseURL}/Inventory/ReturnInventoryItem`,
      data,
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }
    ).pipe(
      map(res => res),
      catchError(this.handleError)
    );
  }

  returnAllInventoryItemsByVendorId(vendorId: number, returnReason: string, barcodeIdList: number[]): Observable<any> {
    const encodedReason = encodeURIComponent(returnReason);
    return this.http.post<any>(
      `${this.baseURL}/Inventory/ReturnAllInventoryItemsByVendorId?vendorId=${vendorId}&returnReason=${encodedReason}`,
      barcodeIdList,
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }
    ).pipe(
      map(res => res),
      catchError(this.handleError)
    );
  }

  saveInventoryImage(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseURL}/Inventory/SaveInventoryImage`,
      data,
      { observe: 'response' }
    ).pipe(
      map((res: HttpResponse<any>) => res.body),
      catchError(this.handleError)
    );
  }

  getInventorySearchList(vendorId?: number, categoryId?: number, description?: string): Observable<any> {
    const vId = vendorId ? vendorId : 0;
    const cId = categoryId ? categoryId : 0;
    const desc = description && description.trim() !== '' ? encodeURIComponent(description.trim()) : '';
    let params = `?vendorId=${vId}&categoryId=${cId}&description=${desc}`;

    return this.http.get<any>(
      `${this.baseURL}/Inventory/GetInventorySearchList${params}`,
      { observe: 'response' }
    ).pipe(
      map(res => res.body),
      catchError(this.handleError)
    );
  }

  getBarcodeTemplateSettings(): Observable<any> {
    return this.http.get<any>(
      `${this.baseURL}/Inventory/GetBarcodeTemplateSettings`,
      { observe: 'response' }
    ).pipe(
      map((res: HttpResponse<any>) => res.body),
      catchError(this.handleError)
    );
  }

  saveBarcodeTemplateSetting(payload: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseURL}/Inventory/SaveBarcodeTemplateSetting`,
      payload,
      { observe: 'response' }
    ).pipe(
      map((res: HttpResponse<any>) => res.body),
      catchError(this.handleError)
    );
  }

  validateInventoryStockIds(request: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseURL}/Inventory/ValidateInventoryStockIds`,
      request,
      { observe: 'response' }
    ).pipe(
      map((res: HttpResponse<any>) => res.body),
      catchError(this.handleError)
    );
  }

  private handleError(error: HttpErrorResponse) {
    console.error('server error:', error);
    if (error.error instanceof Error) {
      const errMessage = error.error.message;
      return throwError(errMessage);
    }
    return throwError(error || 'Node.js server error');
  }
}
